import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, PhoneCall, Mail, MessageSquare, StickyNote } from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";

const BUCKETS = [
  { id: "HARD_CANCEL",              label: "Hard cancel",              tone: "bg-[#fdecec] text-[#c73535] border-[#f4c9c9]" },
  { id: "UNCLASSIFIED",             label: "Unclassified",             tone: "bg-[#fff6dc] text-[#8a6a1c] border-[#f0dfa5]" },
  { id: "EXCLUDE",                  label: "Excluded",                 tone: "bg-[#eef1f6] text-[#485563] border-[#dbe0e8]" },
  { id: "STRUCTURAL_HOLD",          label: "Structural hold",          tone: "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]" },
  { id: "MISCLASSIFIED_RESCHEDULE", label: "Misclassified reschedule", tone: "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]" },
];

const ACTIONS = [
  { id: "call",  label: "Recovery call",     Icon: PhoneCall },
  { id: "text",  label: "Text customer",     Icon: MessageSquare },
  { id: "email", label: "Email follow-up",   Icon: Mail },
  { id: "note",  label: "Add internal note", Icon: StickyNote },
];

// Outcome status enum — mirrors backend FOLLOWUP_STATUS in models.py.
// Order matches typical decision-tree order (attempt → outcome branches).
const STATUS_OPTIONS = [
  { id: "attempted",   label: "Attempted (no response yet)", pillClass: "bg-[#f5f6f8] text-[#4b5563] border-[#e0e3ea]" },
  { id: "connected",   label: "Connected (spoke / heard back)", pillClass: "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]" },
  { id: "rescheduled", label: "Rescheduled (recovery win)", pillClass: "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]" },
  { id: "lost",        label: "Lost (final cancellation)", pillClass: "bg-[#fdecec] text-[#c73535] border-[#f4c9c9]" },
  { id: "no_answer",   label: "No answer (dead-end contact)", pillClass: "bg-[#fff6dc] text-[#8a6a1c] border-[#f0dfa5]" },
];
const STATUS_BY_ID = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.id, s]));

// Per-unit cancellation-rate strip. Source: Claire's Big-Board PDF
// extraction (L10Scorecard.cancellation_by_unit). Renders one badge per
// unit key. Only mounts when the extract has run for the current week —
// no fabricated fallback if `cancellation_by_unit` is null/empty.
function PerUnitCancelStrip({ perUnit, weekOf }) {
  const rateThreshold = { green: 10, yellow: 20 };  // ≤10% green, ≤20% yellow, >20% red
  const toneFor = (pct) => {
    if (pct <= rateThreshold.green) return "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]";
    if (pct <= rateThreshold.yellow) return "bg-[#fff6dc] text-[#8a6a1c] border-[#f0dfa5]";
    return "bg-[#fdecec] text-[#c73535] border-[#f4c9c9]";
  };
  const prettyName = (key) => {
    // Claire posts lowercase snake_case ("central_mo", "springfield"). Prettify
    // for display: title-case each word, uppercase 2-3 letter state abbrevs.
    return key.split(/[_\-\s]+/).map((w) => {
      if (w.length <= 3 && w === w.toLowerCase()) return w.toUpperCase();
      return w.charAt(0).toUpperCase() + w.slice(1);
    }).join(" ");
  };
  const entries = Object.entries(perUnit).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <section
      data-testid="cancel-per-unit-strip"
      className="bg-white border border-[#e7e9ee] rounded-xl p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">
          Cancellation rate by unit
        </div>
        <span
          className="text-[9.5px] font-semibold uppercase tracking-wider bg-[#fff7d6] text-[#8a6100] border border-[#f0dfa2] rounded px-1.5 py-0.5"
          data-testid="cancel-per-unit-source-pill"
          title="AI-extracted from ServiceTitan's Big Board PDF by Claire (VPS agent). Windowed to the L10 reporting week."
        >
          Source: Claire
        </span>
        {weekOf && (
          <span className="ml-auto text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">
            week of {weekOf}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {entries.map(([unit, pct]) => (
          <div
            key={unit}
            data-testid={`cancel-per-unit-badge-${unit}`}
            className={`inline-flex items-baseline gap-2 rounded-lg border px-3 py-2 ${toneFor(pct)}`}
          >
            <span className="text-[11px] font-semibold uppercase tracking-wider">{prettyName(unit)}</span>
            <span className="font-mono-data font-bold text-[18px] leading-none">{Number(pct).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function FollowupModal({ cancel, onClose }) {
  const [items, setItems] = useState([]);
  const [action, setAction] = useState("call");
  const [status, setStatus] = useState("attempted");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/cancellations/${cancel.id}/followups`).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [cancel.id]);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/cancellations/${cancel.id}/followups`, {
        action_type: action,
        status,
        note,
      });
      setNote("");
      setStatus("attempted");
      await load();
    }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="cancel-followup-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[600px] my-4 shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-[#eef1f6] flex items-start justify-between gap-3">
          <div>
            <div className="text-[13px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold">{cancel.job_id} · {cancel.cancelled_at ? new Date(cancel.cancelled_at).toLocaleDateString() : "Date unknown"}</div>
            <div className="font-display font-bold text-[19px] text-[#0e1420]" data-testid="cancel-modal-customer">
              {cancel.customer
                ? cancel.customer
                : <span className="italic text-[color:var(--cc-ink-muted)]">No customer on file</span>}
            </div>
            <div className="text-[14px] text-[color:var(--cc-ink-muted)] mt-0.5">{cancel.reason}</div>
          </div>
          <button onClick={onClose} data-testid="cancel-followup-close" className="p-2 rounded-md hover:bg-[#f5f6f8]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 border-b border-[#eef1f6]">
          <div className="text-[12px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold mb-2">Take action</div>
          <div className="grid grid-cols-2 gap-2">
            {ACTIONS.map(({ id, label, Icon }) => (
              <button key={id} type="button" onClick={() => setAction(id)} data-testid={`followup-action-${id}`}
                className={`flex items-center gap-2 text-[13px] font-semibold rounded-md border px-3 py-2 transition-colors ${action === id ? "bg-[#0072bc] text-white border-[#0072bc]" : "bg-white text-[#0e1420] border-[#e7e9ee] hover:border-[#0072bc]/60"}`}>
                <Icon size={15} strokeWidth={1.8} /> {label}
              </button>
            ))}
          </div>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notes…" data-testid="followup-note-input"
            className="w-full mt-3 border border-[#e7e9ee] rounded-md px-3 py-2 text-[14px] outline-none focus:border-[#0072bc]" />
          <div className="mt-3">
            <div className="text-[12px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Outcome</div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="followup-status-select"
              className="w-full border border-[#e7e9ee] rounded-md px-3 py-2 text-[14px] outline-none focus:border-[#0072bc] bg-white"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
          <button type="submit" disabled={saving} data-testid="followup-submit" className="cc-btn-primary text-[13px] mt-3 disabled:opacity-60">{saving ? "Saving…" : "Log action"}</button>
        </form>
        <div className="p-5 max-h-[40vh] overflow-y-auto">
          <div className="text-[12px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold mb-2">History · {items.length}</div>
          {items.length === 0 && <div className="text-[14px] text-[color:var(--cc-ink-muted)]">No actions logged yet.</div>}
          <ul className="space-y-2">
            {items.map((f) => {
              const statusDef = STATUS_BY_ID[f.status] || { label: f.status || "—", pillClass: "bg-[#f5f6f8] text-[#4b5563] border-[#e0e3ea]" };
              const isAgent = f.created_by && !f.created_by.includes("@");
              return (
                <li key={f.id} className="border border-[#eef1f6] rounded-md px-3 py-2 text-[13.5px]" data-testid={`followup-row-${f.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold uppercase text-[11.5px] text-[#0072bc]">{f.action_type}</span>
                      <span
                        className={`inline-block text-[10px] font-mono-data uppercase tracking-widest border rounded px-1.5 py-0.5 ${statusDef.pillClass}`}
                        data-testid={`followup-row-status-${f.id}`}
                      >
                        {f.status}
                      </span>
                    </div>
                    <span className="text-[11.5px] text-[color:var(--cc-ink-muted)]">{new Date(f.created_at).toLocaleString()}</span>
                  </div>
                  {f.note && <div className="text-[13.5px] text-[#0e1420] mt-1">{f.note}</div>}
                  <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5 flex items-center gap-1.5">
                    <span>by {f.created_by}</span>
                    {isAgent && (
                      <span className="inline-block text-[9px] font-mono-data uppercase tracking-widest border rounded px-1 py-0.5 bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]" data-testid={`followup-row-agent-tag-${f.id}`}>
                        Agent
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function Cancellations() {
  const [summary, setSummary] = useState([]);
  const [active, setActive] = useState("HARD_CANCEL");
  const [list, setList] = useState([]);
  const [openCancel, setOpenCancel] = useState(null);
  const [summaryState, setSummaryState] = useState({ status: "loading", error: null });
  const [listState, setListState] = useState({ status: "loading", error: null });
  // Per-unit cancellation-rate strip driven by Claire's Big-Board PDF
  // extract (L10Scorecard.cancellation_by_unit, wired live 2026-07-30
  // via VPS commit b06ebd9). Windowed to the L10 reporting week. Only
  // renders when Claire actually populated the field — no fabricated
  // fallback if the extract hasn't run yet.
  const [perUnit, setPerUnit] = useState(null);        // {springfield: 8.89, ...} | null
  const [perUnitWeek, setPerUnitWeek] = useState(null);
  const perUnitLoad = async () => {
    try {
      const { data } = await api.get("/l10/scorecard/vps-snapshot");
      const snap = data?.snapshot || null;
      setPerUnit(snap?.cancellation_by_unit || null);
      setPerUnitWeek(snap?.week_of || data?.week_of || null);
    } catch {
      setPerUnit(null);
    }
  };

  const loadSummary = async () => {
    setSummaryState({ status: "loading", error: null });
    try {
      const { data } = await api.get("/cancellations/buckets");
      setSummary(data || []);
      setSummaryState({ status: "ok", error: null });
    } catch (err) {
      setSummary([]);
      setSummaryState({ status: "error", error: extractApiError(err) });
    }
  };
  const loadList = async (bucket) => {
    setListState({ status: "loading", error: null });
    try {
      const { data } = await api.get(`/cancellations?bucket=${bucket}`);
      setList(data || []);
      setListState({ status: "ok", error: null });
    } catch (err) {
      setList([]);
      setListState({ status: "error", error: extractApiError(err) });
    }
  };
  useEffect(() => { loadSummary(); perUnitLoad(); }, []);
  useEffect(() => { loadList(active); }, [active]);

  const findCount = (id) => summary.find((s) => s.bucket === id)?.count || 0;
  const totalCount = summary.reduce((a, b) => a + b.count, 0);
  const activeBucketLabel = BUCKETS.find((b) => b.id === active)?.label || active;
  return (
    <div data-testid="cancellations-page" className="space-y-5">
      <header>
        <div className="text-[12px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold">Sam · Agent 4B · Daily 11:45 AM</div>
        <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1">Cancellations</h1>
        <p className="text-[14px] text-[color:var(--cc-ink-muted)] mt-1">Five-bucket classifier · {totalCount} jobs · click any row to take a recovery action.</p>
      </header>

      <PanelState
        status={summaryState.status}
        error={summaryState.error}
        isEmpty={summaryState.status === "ok" && summary.length === 0}
        onRetry={loadSummary}
        emptyTitle="No cancellation data yet"
        emptyBody={<>Sam hasn’t classified any cancellations for this tenant yet.</>}
        testIdPrefix="cancel-summary"
      />

      {summaryState.status === "ok" && summary.length > 0 && (
        <>
          {perUnit && Object.keys(perUnit).length > 0 && (
            <PerUnitCancelStrip perUnit={perUnit} weekOf={perUnitWeek} />
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {BUCKETS.map((b) => {
              const isActive = b.id === active;
              return (
                <button key={b.id} onClick={() => setActive(b.id)} data-testid={`cancel-bucket-btn-${b.id}`}
                  className={`text-left rounded-xl border p-4 transition-all ${isActive ? "border-[#0072bc] shadow-sm -translate-y-[1px] bg-white" : "border-[#e7e9ee] bg-white hover:border-[#0072bc]/50"}`}>
                  <div className={`inline-flex text-[11.5px] font-semibold uppercase tracking-wider border rounded px-2 py-0.5 ${b.tone}`}>{b.label}</div>
                  <div className="mt-3 font-mono-data font-bold text-[26px] text-[#0e1420]">{findCount(b.id)}</div>
                </button>
              );
            })}
          </div>

          <PanelState
            status={listState.status}
            error={listState.error}
            isEmpty={listState.status === "ok" && list.length === 0}
            onRetry={() => loadList(active)}
            emptyTitle={`No cancellations in "${activeBucketLabel}"`}
            emptyBody={<>Nothing has landed in this bucket yet. Pick another bucket above.</>}
            testIdPrefix="cancel-list"
          />

          {listState.status === "ok" && list.length > 0 && (() => {
            /* Split into two groups so dateless rows never silently sort to
               top-or-bottom of a date-descending list — they render as a clearly
               separated "Date unknown" block AFTER the dated rows.
               The dateless group is rendered independently of the dated cap so
               it's always visible even when dated rows fill the top-40 window. */
            const dated = list.filter((c) => !!c.cancelled_at)
                              .sort((a, b) => (b.cancelled_at || "").localeCompare(a.cancelled_at || ""));
            const dateless = list.filter((c) => !c.cancelled_at);
            const visibleDated = dated.slice(0, 40);
            const visibleDateless = dateless.slice(0, 40);
            const renderRow = (c, isDateless) => (
              <tr key={c.id} className="border-t border-[#eef1f6] hover:bg-[#fafbfc] cursor-pointer" onClick={() => setOpenCancel(c)} data-testid={`cancel-row-${c.id}`}>
                <td className="py-2.5 px-5 font-mono-data text-[13px]">{c.job_id}</td>
                <td className="py-2.5 px-5" data-testid={`cancel-row-customer-${c.id}`}>
                  {c.customer
                    ? c.customer
                    : <span className="text-[13px] text-[color:var(--cc-ink-muted)] italic">No customer on file</span>}
                </td>
                <td className="py-2.5 px-5 text-[color:var(--cc-ink-muted)]">{c.reason}</td>
                <td className="py-2.5 px-5 font-mono-data text-[13px] text-[color:var(--cc-ink-muted)]" data-testid={isDateless ? `cancel-date-unknown-${c.id}` : undefined}>
                  {isDateless
                    ? <span className="inline-block bg-[#f5f6f8] border border-[#e0e3ea] rounded px-1.5 py-0.5 text-[11px] uppercase tracking-widest font-semibold text-[#4b5563]">Date unknown</span>
                    : new Date(c.cancelled_at).toLocaleDateString()}
                </td>
                <td className="py-2.5 px-5 text-right">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpenCancel(c); }}
                    data-testid={`cancel-row-followup-btn-${c.id}`}
                    className="inline-flex items-center gap-1 text-[13px] text-[#0072bc] font-semibold rounded-md border border-[#c5e0ee] bg-[#e7f4fb] px-2.5 py-1 hover:bg-[#d5eaf5] focus:outline-none focus:ring-2 focus:ring-[#0072bc]/40 transition-colors"
                    aria-label={`Log follow-up for job ${c.job_id}`}
                  >
                    Follow up →
                  </button>
                </td>
              </tr>
            );
            return (
              <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-x-auto" data-testid="cancel-list">
                <div className="px-5 py-3 border-b border-[#eef1f6] flex items-center justify-between gap-3">
                  <div className="font-display font-semibold text-[15px]">
                    {activeBucketLabel} · {list.length}
                    {dateless.length > 0 && (
                      <span className="ml-2 text-[11.5px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)]" data-testid="cancel-dateless-count">
                        · {dateless.length} dateless
                      </span>
                    )}
                  </div>
                </div>
                <table className="w-full min-w-[640px] text-[14px]">
                  <thead className="bg-[#f5f6f8] text-[11.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
                    <tr><th className="text-left py-2.5 px-5">Job</th><th className="text-left py-2.5 px-5">Customer</th><th className="text-left py-2.5 px-5">Reason</th><th className="text-left py-2.5 px-5">Cancelled</th><th className="text-right py-2.5 px-5">Action</th></tr>
                  </thead>
                  <tbody>
                    {visibleDated.map((c) => renderRow(c, false))}
                    {visibleDateless.length > 0 && (
                      <tr className="bg-[#fafbfc] border-t-2 border-[#e0e3ea]" data-testid="cancel-dateless-divider">
                        <td colSpan={5} className="py-2 px-5 text-[11px] uppercase tracking-[.12em] font-bold text-[color:var(--cc-ink-muted)]">
                          Date unknown · {dateless.length} row{dateless.length === 1 ? "" : "s"} — excluded from week-scoped rate math
                        </td>
                      </tr>
                    )}
                    {visibleDateless.map((c) => renderRow(c, true))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </>
      )}

      {openCancel && <FollowupModal cancel={openCancel} onClose={() => setOpenCancel(null)} />}
    </div>
  );
}
